/**
 * Centralized Frontend Trace Manager
 * Creates a single parent trace for the entire frontend session
 * All other traces/spans become children of this parent
 */

import { telemetry, SpanStatusCode } from '@/telemetry-browser';
import { trace, context, Span, Context } from '@opentelemetry/api';

interface FrontendSession {
  userId?: string;
  organizationId?: string;
  sessionId: string;
  userAgent: string;
  timestamp: string;
}

class FrontendTraceManager {
  private parentSpan: Span | null = null;
  private parentContext: Context | null = null;
  private sessionId: string;
  private sessionData: FrontendSession | null = null;
  private isActive: boolean = false;

  constructor() {
    this.sessionId = this.generateSessionId();
  }

  /**
   * Initialize the single parent trace for the entire frontend session
   */
  initializeSession(sessionData: Partial<FrontendSession> = {}) {
    if (this.isActive && this.parentSpan) {
      console.log('🌟 Frontend session already active, skipping duplicate initialization');
      return this.parentContext;
    }

    this.sessionData = {
      sessionId: this.sessionId,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      ...sessionData,
    };

    console.log('🌟 Initializing SINGLE parent trace for entire frontend session:', this.sessionId);
    console.log('🔍 Telemetry configuration check:', {
      telemetryExists: !!telemetry,
      telemetryMethods: telemetry ? Object.keys(telemetry) : 'none',
      environment: import.meta.env.MODE,
      otlpEndpoint: import.meta.env.VITE_OTEL_ENDPOINT,
      consoleExport: import.meta.env.VITE_OTEL_CONSOLE_EXPORT,
    });

    // Test basic telemetry first
    console.log('🧪 Testing basic telemetry span creation...');
    try {
      const testSpan = telemetry.startSpan('test.basic_span', {
        attributes: { 'test.timestamp': Date.now() }
      });
      console.log('🧪 Basic test span created successfully:', {
        traceId: testSpan.spanContext().traceId,
        spanId: testSpan.spanContext().spanId,
        isRecording: testSpan.isRecording(),
      });
      testSpan.end();
    } catch (error) {
      console.error('🚨 Failed to create basic test span:', error);
      return null;
    }

    this.parentSpan = telemetry.startSpan('frontend.session', {
      attributes: {
        'frontend.session.id': this.sessionId,
        'frontend.session.user_id': this.sessionData.userId || 'anonymous',
        'frontend.session.organization_id': this.sessionData.organizationId || 'unknown',
        'frontend.session.user_agent': this.sessionData.userAgent,
        'frontend.session.started_at': this.sessionData.timestamp,
        'frontend.session.url': window.location.href,
        'frontend.session.pathname': window.location.pathname,
        'frontend.session.browser.language': navigator.language,
        'frontend.session.browser.platform': navigator.platform,
        'frontend.session.screen.width': screen.width,
        'frontend.session.screen.height': screen.height,
        'frontend.session.viewport.width': window.innerWidth,
        'frontend.session.viewport.height': window.innerHeight,
      },
    });

    // Set this as the active context for all child spans
    this.parentContext = trace.setSpan(context.active(), this.parentSpan);
    this.isActive = true;

    // Add session start event
    this.parentSpan.addEvent('frontend_session_started', {
      session_id: this.sessionId,
      timestamp: this.sessionData.timestamp,
      url: window.location.href,
    });

    // Listen for page navigation to add events
    this.setupNavigationTracking();
    
    // Listen for errors to capture them in parent trace
    this.setupErrorTracking();

    console.log('🌟 Frontend parent trace initialized successfully:', {
      sessionId: this.sessionId,
      traceId: this.parentSpan.spanContext().traceId,
      spanId: this.parentSpan.spanContext().spanId,
    });

    return this.parentContext;
  }

  /**
   * Create a child span under the main frontend session trace
   */
  createChildSpan(name: string, options: { attributes?: Record<string, any> } = {}) {
    if (!this.parentSpan || !this.parentContext) {
      console.warn('⚠️ No parent span/context active, cannot create child span');
      return null;
    }

    const childSpan = telemetry.startSpan(name, {
      attributes: {
        'frontend.session.id': this.sessionId,
        'span.parent': 'frontend_session',
        'span.created_at': new Date().toISOString(),
        ...options.attributes,
      },
    });

    console.log(`🔗 Created child span '${name}' under parent trace ${this.sessionId}`);
    return childSpan;
  }

  /**
   * Add an event to the main frontend session trace
   */
  addEvent(name: string, attributes: Record<string, any> = {}) {
    if (!this.parentSpan) {
      console.warn('⚠️ No parent span active, cannot add event');
      return;
    }

    this.parentSpan.addEvent(name, {
      'frontend.session.id': this.sessionId,
      'event.timestamp': new Date().toISOString(),
      'event.source': 'frontend_trace_manager',
      ...attributes,
    });

    console.log(`📋 Added event '${name}' to parent trace ${this.sessionId}`);
  }

  /**
   * Execute code within the parent trace context
   */
  withParentContext<T>(fn: () => T): T {
    if (!this.parentContext) {
      console.warn('⚠️ No parent context available, executing without trace context');
      return fn();
    }

    return context.with(this.parentContext, fn);
  }

  /**
   * Add an event to the parent trace
   */
  addSessionEvent(name: string, attributes: Record<string, any> = {}) {
    if (!this.parentSpan) {
      console.warn('⚠️ No parent span active, cannot add session event');
      return;
    }

    this.parentSpan.addEvent(name, {
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      ...attributes,
    });

    console.log(`📝 Added session event '${name}' to parent trace`);
  }

  /**
   * Update session data (e.g., when user logs in)
   */
  updateSession(updates: Partial<FrontendSession>) {
    if (!this.parentSpan || !this.sessionData) {
      console.warn('⚠️ No active session to update');
      return;
    }

    this.sessionData = { ...this.sessionData, ...updates };

    // Update span attributes
    this.parentSpan.setAttributes({
      'frontend.session.user_id': this.sessionData.userId || 'anonymous',
      'frontend.session.organization_id': this.sessionData.organizationId || 'unknown',
      'frontend.session.updated_at': new Date().toISOString(),
    });

    this.addSessionEvent('session_updated', updates);
    console.log('🔄 Updated frontend session:', updates);
  }

  /**
   * End the frontend session
   */
  endSession(reason: string = 'session_end') {
    if (!this.parentSpan || !this.isActive) {
      console.log('🌟 No active frontend session to end');
      return;
    }

    console.log(`🌟 Ending frontend session ${this.sessionId}, reason: ${reason}`);

    // Add final session event
    this.parentSpan.addEvent('frontend_session_ended', {
      session_id: this.sessionId,
      end_reason: reason,
      timestamp: new Date().toISOString(),
      duration_ms: Date.now() - new Date(this.sessionData!.timestamp).getTime(),
    });

    // Set final attributes
    this.parentSpan.setAttributes({
      'frontend.session.ended_at': new Date().toISOString(),
      'frontend.session.end_reason': reason,
      'frontend.session.duration_ms': Date.now() - new Date(this.sessionData!.timestamp).getTime(),
    });

    this.parentSpan.setStatus({ code: SpanStatusCode.OK });
    this.parentSpan.end();

    // Reset state
    this.parentSpan = null;
    this.parentContext = null;
    this.isActive = false;
    this.sessionData = null;

    console.log('🌟 Frontend session ended successfully');
  }

  /**
   * Get current session info
   */
  getSessionInfo() {
    return {
      isActive: this.isActive,
      sessionId: this.sessionId,
      sessionData: this.sessionData,
      hasParentSpan: !!this.parentSpan,
      traceId: this.parentSpan?.spanContext().traceId,
      spanId: this.parentSpan?.spanContext().spanId,
    };
  }

  private generateSessionId(): string {
    return `frontend-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private setupNavigationTracking() {
    // Track page navigation
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function(...args) {
      frontendTraceManager.addSessionEvent('navigation', {
        type: 'pushState',
        url: args[2]?.toString() || window.location.href,
      });
      return originalPushState.apply(this, args);
    };

    history.replaceState = function(...args) {
      frontendTraceManager.addSessionEvent('navigation', {
        type: 'replaceState', 
        url: args[2]?.toString() || window.location.href,
      });
      return originalReplaceState.apply(this, args);
    };

    window.addEventListener('popstate', () => {
      this.addSessionEvent('navigation', {
        type: 'popstate',
        url: window.location.href,
      });
    });
  }

  private setupErrorTracking() {
    window.addEventListener('error', (event) => {
      if (this.parentSpan) {
        this.parentSpan.recordException(event.error || new Error(event.message));
        this.addSessionEvent('error', {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        });
      }
    });

    window.addEventListener('unhandledrejection', (event) => {
      if (this.parentSpan) {
        this.parentSpan.recordException(
          event.reason instanceof Error ? event.reason : new Error(String(event.reason))
        );
        this.addSessionEvent('unhandled_promise_rejection', {
          reason: String(event.reason),
        });
      }
    });
  }
}

// Create singleton instance
export const frontendTraceManager = new FrontendTraceManager();

// Global access for debugging
if (typeof window !== 'undefined') {
  (window as any).__frontendTraceManager = frontendTraceManager;
}